'use client'

import { searchBlogs } from '@/lib/actions/blogs/get/searchBlogs';
import { useGetBlogs } from '@/lib/hooks/blogs/get/useGetBlogs';
import React, { useCallback, useEffect, useState } from 'react';
import BlogLeftSide from './BlogLeftSide';
import BlogRightSide from './BlogRightSide';
import { BlogPageSkeleton } from './BlogSkeleton';
import RandomBlog from './RandomBlog';
import { Separator } from '@/components/ui/separator';

interface PaginatedBlogsInterface {
    isEditor?: boolean;
    blogCategories: BlogCategory[];
}

const debounce = (fn: Function, delay: number) => {
    let timeoutId: ReturnType<typeof setTimeout>;
    return (...args: any[]) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
            fn(...args);
        }, delay);
    };
};
const Loader = () => (
    <BlogPageSkeleton />
);

const PaginatedBlogs = ({ isEditor, blogCategories = [] }: PaginatedBlogsInterface) => {
    const [page, setPage] = useState(1);
    const [selectedCategory, setSelectedCategory] = useState<string | undefined>(undefined);

    const { data, isLoading } = useGetBlogs(page, 9, selectedCategory);
    const blogs = data as Blog[];

    // Search states
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState<Blog[]>([]);
    const [isSearching, setIsSearching] = useState(false);

    // Debounced search
    const performSearch = useCallback(
        debounce(async (query: string) => {
            if (!query.trim()) {
                setSearchResults([]);
                setIsSearching(false);
                return;
            }

            setIsSearching(true);
            try {
                const result = await searchBlogs(query);
                setSearchResults(result);
            } catch (error) {
                console.error("Search error:", error);
                setSearchResults([]);
            } finally {
                setIsSearching(false);
            }
        }, 500),
        []
    );

    const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        setSearchTerm(val);
        performSearch(val);
    };

    // Reset search on category change
    useEffect(() => {
        setSearchTerm('');
        setSearchResults([]);
        setPage(1);
    }, [selectedCategory]);

    // Show loader while fetching blogs
    if (isLoading) return <div className='min-h-screen flex justify-center items-center'>
        <Loader />
    </div>;

    const displayBlogs = searchTerm.trim() ? searchResults : blogs;

    console.log("Serarch blogs: ", displayBlogs)
    return (
        <>
            <div className={`p-2 flex flex-col gap-4 mt-10 w-full sm:hidden`}>
                <BlogLeftSide
                    searchTerm={searchTerm}
                    onSearchChange={onSearchChange}
                    blogCategories={blogCategories}
                    selectedCategory={selectedCategory}
                    setSelectedCategory={setSelectedCategory}
                    page={page}
                    setPage={setPage}
                />
                <RandomBlog />
                <Separator />
                <BlogRightSide
                    displayBlogs={displayBlogs}
                    searchTerm={searchTerm}
                    isEditor={isEditor}
                    page={page}
                    setPage={setPage}
                />
            </div>
            <div className="min-h-screen bg-background p-4 sm:p-8">
                <div className="max-w-7xl mx-auto">
                    <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
                        {/* Blog Sidebar */}
                        <div className="lg:col-span-1">
                            <BlogLeftSide
                                searchTerm={searchTerm}
                                onSearchChange={onSearchChange}
                                blogCategories={blogCategories}
                                selectedCategory={selectedCategory}
                                setSelectedCategory={setSelectedCategory}
                                page={page}
                                setPage={setPage}
                            />
                        </div>

                        {/* Main Content Area */}
                        <div className="lg:col-span-3 h-full">
                            <div className="bg-card rounded-xl p-6 border shadow-sm">
                                <div className="space-y-4">
                                    <div className="text-sm text-muted-foreground">
                                        {searchTerm && (
                                            <p>
                                                Search term: <span className="font-medium text-foreground">"{searchTerm}"</span>
                                            </p>
                                        )}
                                        {selectedCategory && (
                                            <p>
                                                Category: <span className="font-medium text-foreground">{selectedCategory}</span>
                                            </p>
                                        )}
                                        {!searchTerm && !selectedCategory && <p>Showing all blog posts</p>}
                                    </div>

                                    <BlogRightSide
                                        displayBlogs={displayBlogs}
                                        searchTerm={searchTerm}
                                        isEditor={isEditor}
                                        page={page}
                                        setPage={setPage}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default PaginatedBlogs;
